import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  Text,
  TextField,
  Callout,
} from "@radix-ui/themes";
import { Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/providers/AuthProvider";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import Select from "@/components/ui/Select";

export type RewardForm = {
  type: string;
  days?: number;
  amount?: number;
  currency?: string;
  percent_off?: number;
};

export function parseReward(json: string | null | undefined): RewardForm | null {
  if (!json || json === "null") return null;
  try {
    return JSON.parse(json) as RewardForm;
  } catch {
    return null;
  }
}

export function serialiseReward(reward: RewardForm): string | null {
  if (reward.type === "none") return null;
  return JSON.stringify(reward);
}

export function rewardSummary(json: string | null | undefined): string {
  const reward = parseReward(json);
  if (!reward || reward.type === "none") return "None";
  if (reward.type === "trial_extension") return `+${reward.days ?? 0} day trial`;
  if (reward.type === "subscription_extension")
    return `+${reward.days ?? 0} day subscription`;
  if (reward.type === "points")
    return `+${reward.amount ?? 0} points`;
  if (reward.type === "credit")
    return `+${reward.amount ?? 0} ${(reward.currency ?? "").toUpperCase()} credit`;
  if (reward.type === "cash_payout")
    return `${reward.amount ?? 0} ${reward.currency ?? ""} cash`;
  if (reward.type === "discount") return `${reward.percent_off ?? 0}% off`;
  return reward.type;
}

export function statusColor(status: string): "green" | "amber" | "gray" {
  if (status === "active") return "green";
  if (status === "paused") return "amber";
  return "gray";
}

export function rewardStatusColor(
  status: string | null | undefined,
): "green" | "amber" | "red" | "gray" {
  if (status === "applied") return "green";
  if (status === "pending") return "amber";
  if (status === "failed") return "red";
  return "gray";
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge color={statusColor(status)} variant="soft">{status}</Badge>;
}

// What the tenant's AnythinkPay processors will currently support — the create/edit forms
// disable reward types that require a processor that isn't configured.
export function useAnythinkPayCapabilities() {
  const { currentTenant } = useAuth();
  const { data: appleCreds } = useQuery({
    queryKey: ["apple-iap-credentials"],
    queryFn: () => anythinkPayService.getAppleIapCredentials(),
    staleTime: 60_000,
  });
  const stripeConfigured = Boolean(currentTenant?.stripe_connect_onboarding_completed);
  const appleConfigured = Boolean(appleCreds?.is_configured);
  return { stripeConfigured, appleConfigured };
}

// Audience: who this reward applies to.
//   - "redeemer" = the new user using the code; everything must be available without a payment
//     processor for the redeemer side (we can always extend their trial).
//   - "referrer" = the existing user who owns the personal code; same trial-extension rules apply,
//     but cash payout needs Stripe Connect to actually transfer.
type RewardAudience = "redeemer" | "referrer";

export function RewardPickerSection({
  label,
  reward,
  onChange,
  audience,
  usePortal = false,
}: {
  label: string;
  reward: RewardForm;
  onChange: (r: RewardForm) => void;
  audience: RewardAudience;
  usePortal?: boolean;
}) {
  const { stripeConfigured } = useAnythinkPayCapabilities();
  const showDays =
    reward.type === "trial_extension" || reward.type === "subscription_extension";
  const showPoints = reward.type === "points";
  const showCredit = reward.type === "credit";
  const showCash = reward.type === "cash_payout";
  const showDiscount = reward.type === "discount";

  const options = [
    { value: "none", label: "None", note: "No reward" },
    { value: "trial_extension", label: "Extend free trial", note: "Add N days to the engagement trial" },
    { value: "subscription_extension", label: "Extend subscription", note: "Push renewal date forward (Stripe or Apple)" },
    { value: "points", label: "Points", note: "Credit loyalty points to the user's balance" },
    { value: "credit", label: "Account credit", note: "Credit the user's tenant-side currency balance" },
    {
      value: "cash_payout",
      label: "Cash payout",
      disabled: audience !== "referrer" || !stripeConfigured,
      note:
        audience !== "referrer"
          ? "Only available as a referrer reward"
          : !stripeConfigured
          ? "Configure Stripe Connect in AnythinkPay"
          : "Applied as customer balance credit on next invoice",
    },
    {
      value: "discount",
      label: "Discount",
      disabled: !stripeConfigured,
      note: !stripeConfigured
        ? "Configure Stripe in AnythinkPay"
        : "Stripe coupon applied at checkout",
    },
  ];

  return (
    <Box>
      <Text size="2" weight="medium" as="div" mb="1">{label}</Text>
      <Select
        value={reward.type}
        onChange={(v) => onChange({ ...reward, type: v })}
        options={options}
        usePortal={usePortal}
      />

      {showDays && (
        <Box mt="2">
          <Text size="2" weight="medium" as="div" mb="1">Days</Text>
          <TextField.Root
            type="number"
            min="1"
            value={(reward.days ?? "").toString()}
            onChange={(e) =>
              onChange({ ...reward, days: parseInt(e.target.value || "0", 10) })
            }
          />
        </Box>
      )}
      {showCash && (
        <Grid columns="2" gap="2" mt="2">
          <Box>
            <Text size="2" weight="medium" as="div" mb="1">Amount</Text>
            <TextField.Root
              type="number"
              min="0"
              step="0.01"
              value={(reward.amount ?? "").toString()}
              onChange={(e) =>
                onChange({ ...reward, amount: parseFloat(e.target.value || "0") })
              }
            />
          </Box>
          <Box>
            <Text size="2" weight="medium" as="div" mb="1">Currency</Text>
            <TextField.Root
              value={reward.currency ?? ""}
              onChange={(e) =>
                onChange({ ...reward, currency: e.target.value.toUpperCase() })
              }
              placeholder="GBP"
              maxLength={3}
            />
          </Box>
        </Grid>
      )}
      {showPoints && (
        <Box mt="2" style={{ maxWidth: 200 }}>
          <Text size="2" weight="medium" as="div" mb="1">Points</Text>
          <TextField.Root
            type="number"
            min="1"
            value={(reward.amount ?? "").toString()}
            onChange={(e) =>
              onChange({ ...reward, amount: parseInt(e.target.value || "0", 10) })
            }
            placeholder="100"
          />
        </Box>
      )}
      {showCredit && (
        <Grid columns="2" gap="2" mt="2">
          <Box>
            <Text size="2" weight="medium" as="div" mb="1">Amount</Text>
            <TextField.Root
              type="number"
              min="0"
              step="0.01"
              value={(reward.amount ?? "").toString()}
              onChange={(e) =>
                onChange({ ...reward, amount: parseFloat(e.target.value || "0") })
              }
              placeholder="5.00"
            />
          </Box>
          <Box>
            <Text size="2" weight="medium" as="div" mb="1">Currency</Text>
            <TextField.Root
              value={reward.currency ?? ""}
              onChange={(e) =>
                onChange({ ...reward, currency: e.target.value.toUpperCase() })
              }
              placeholder="GBP"
              maxLength={3}
            />
          </Box>
        </Grid>
      )}
      {showDiscount && (
        <Box mt="2">
          <Text size="2" weight="medium" as="div" mb="1">Percent off</Text>
          <TextField.Root
            type="number"
            min="0"
            max="100"
            value={(reward.percent_off ?? "").toString()}
            onChange={(e) =>
              onChange({ ...reward, percent_off: parseFloat(e.target.value || "0") })
            }
            placeholder="50"
          />
        </Box>
      )}
    </Box>
  );
}

export function ConfigureProcessorHint({ orgId }: { orgId: string | number | undefined }) {
  return (
    <Callout.Root color="amber" size="1">
      <Callout.Icon>
        <Info size={14} />
      </Callout.Icon>
      <Callout.Text>
        Cash payouts and discount rewards need a payment processor configured.{" "}
        <a
          href={`/org/${orgId}/integrations/anythinkpay`}
          style={{ color: "var(--accent-11)", textDecoration: "underline" }}
        >
          Configure AnythinkPay
        </a>{" "}
        to unlock them.
      </Callout.Text>
    </Callout.Root>
  );
}

export function EmptyStateCard({
  icon,
  title,
  description,
  cta,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta?: React.ReactNode;
}) {
  return (
    <Card>
      <Flex direction="column" align="center" justify="center" gap="3" p="6">
        <Box style={{ color: "var(--gray-9)" }}>{icon}</Box>
        <Text size="4" weight="bold">{title}</Text>
        <Text size="2" color="gray" align="center" style={{ maxWidth: 480 }}>
          {description}
        </Text>
        {cta}
      </Flex>
    </Card>
  );
}
